(function () {
    if (window.EEGPredictiveSearch) return;

    const CACHE_KEY = 'EEGPredictiveCache';
    const LIMIT_KEY = 'EEGPredictiveCacheLimit';
    const STORAGE_KEY = 'eegPredictiveCache';

    const formatMoney = (centsValue, moneyFormat) => {
        if (centsValue === null || centsValue === undefined) return '';
        let cents = centsValue;
        if (typeof cents === 'string') {
            const normalized = cents.replace(/[^0-9.-]/g, '');
            cents = normalized === '' ? cents : Number(normalized);
        }
        if (typeof cents !== 'number' || Number.isNaN(cents)) {
            return String(centsValue);
        }
        const formatWithDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
            const rounded = (number / 100).toFixed(precision);
            const parts = rounded.split('.');
            const dollars = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
            const centsPart = parts[1] ? decimal + parts[1] : '';
            return dollars + centsPart;
        };
        const format = moneyFormat || '${{amount}}';
        const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
        if (!match) {
            return formatWithDelimiters(cents, 2);
        }
        switch (match[1]) {
            case 'amount':
                return format.replace(match[0], formatWithDelimiters(cents, 2));
            case 'amount_no_decimals':
                return format.replace(match[0], formatWithDelimiters(cents, 0));
            case 'amount_with_comma_separator':
                return format.replace(match[0], formatWithDelimiters(cents, 2, '.', ','));
            case 'amount_no_decimals_with_comma_separator':
                return format.replace(match[0], formatWithDelimiters(cents, 0, '.', ','));
            default:
                return format.replace(match[0], formatWithDelimiters(cents, 2));
        }
    };

    const buildSizedImageUrl = (url, width) => {
        if (!url || !width) return url;
        try {
            const sizedUrl = new URL(url, window.location.origin);
            sizedUrl.searchParams.set('width', String(width));
            return sizedUrl.toString();
        } catch (error) {
            return url;
        }
    };

    const getProductImage = (product) => {
        if (!product) return null;
        const imageData = product.featured_image || product.image;
        if (!imageData) return null;
        if (typeof imageData === 'string') {
            return { url: imageData, alt: product.title || '' };
        }
        const url = imageData.url || imageData.src || '';
        if (!url) return null;
        return { url, alt: imageData.alt || product.title || '' };
    };

    const createArrowIcon = () => {
        if (typeof window.eegCreateArrowIcon === 'function') {
            return window.eegCreateArrowIcon();
        }
        return document.createElement('span');
    };

    const getPredictiveCacheStore = () => {
        if (!(window[CACHE_KEY] instanceof Map)) {
            window[CACHE_KEY] = new Map();
        }
        if (!Number.isFinite(window[LIMIT_KEY])) {
            window[LIMIT_KEY] = 12;
        }
        return { cache: window[CACHE_KEY], limit: window[LIMIT_KEY] };
    };

    const predictiveStorageAvailable = (() => {
        try {
            const testKey = 'eegPredictiveCacheTest';
            sessionStorage.setItem(testKey, '1');
            sessionStorage.removeItem(testKey);
            return true;
        } catch (error) {
            return false;
        }
    })();

    const readPredictiveStorage = () => {
        if (!predictiveStorageAvailable) return [];
        try {
            const raw = sessionStorage.getItem(STORAGE_KEY);
            if (!raw) return [];
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            return [];
        }
    };

    const writePredictiveStorage = (entries) => {
        if (!predictiveStorageAvailable) return;
        try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
        } catch (error) {
            return;
        }
    };

    let cacheHydrated = false;

    const hydratePredictiveCache = (cache, limit) => {
        if (cacheHydrated) return;
        cacheHydrated = true;
        const entries = readPredictiveStorage();
        if (!entries.length) return;
        const trimmedEntries = entries.slice(-limit);
        trimmedEntries.forEach((entry) => {
            if (!entry || typeof entry.query !== 'string') return;
            const suggestions = Array.isArray(entry.suggestions) ? entry.suggestions : [];
            const products = Array.isArray(entry.products) ? entry.products : [];
            if (!cache.has(entry.query)) {
                cache.set(entry.query, { suggestions, products });
            }
        });
        if (trimmedEntries.length !== entries.length) {
            writePredictiveStorage(trimmedEntries);
        }
    };

    const persistPredictiveCache = (queryKey, payload, limit) => {
        if (!predictiveStorageAvailable) return;
        const entries = readPredictiveStorage().filter((entry) => entry && entry.query !== queryKey);
        entries.push({
            query: queryKey,
            suggestions: payload.suggestions || [],
            products: payload.products || [],
        });
        while (entries.length > limit) {
            entries.shift();
        }
        writePredictiveStorage(entries);
    };

    const buildPredictiveUrl = (queryValue, suggestionLimit, productLimit) => {
        const url = new URL('/search/suggest.json', window.location.origin);
        url.searchParams.set('q', queryValue);
        url.searchParams.set('resources[type]', 'product,query');
        url.searchParams.set('resources[limit]', String(Math.max(suggestionLimit, productLimit)));
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        url.searchParams.set('resources[options][fields]', 'title,product_type');
        return url.toString();
    };

    const createPredictiveSearch = (config) => {
        if (!config) return null;
        const input = config.input;
        const panel = config.panel;
        const suggestionsList = config.suggestionsList;
        const productsList = config.productsList;
        if (!input || !panel || !suggestionsList || !productsList) return null;

        const container = config.container || null;
        const containerVisibleClass = config.containerVisibleClass || '';
        const clearButton = config.clearButton || null;
        const footer = config.footer || null;
        const loading = config.loading || null;
        const suggestionLimit = Number(config.suggestionLimit) || 6;
        const productLimit = Number(config.productLimit) || 4;
        const moneyFormat = config.moneyFormat || '${{amount}}';
        const minLength = Number(config.minLength) || 2;
        const debounceMs = Number(config.debounceMs) || 200;
        const blurHideDelay = Number(config.blurHideDelay) || 160;
        const renderSuggestion = typeof config.renderSuggestion === 'function' ? config.renderSuggestion : null;
        const renderProduct = typeof config.renderProduct === 'function' ? config.renderProduct : null;
        const onSubmit = typeof config.onSubmit === 'function' ? config.onSubmit : null;
        const getFooterData = typeof config.getFooterData === 'function' ? config.getFooterData : null;
        const enableKeyboardNavigation = Boolean(config.enableKeyboardNavigation);
        const keyboardActiveClass = config.keyboardActiveClass || 'is-keyboard-active';
        const activeItemIdPrefix = config.activeItemIdPrefix || 'predictive-item';

        if (!renderSuggestion || !renderProduct) return null;

        const { cache: predictiveCache, limit: predictiveCacheLimit } = getPredictiveCacheStore();
        hydratePredictiveCache(predictiveCache, predictiveCacheLimit);

        let predictiveController = null;
        let predictiveTimeout = null;
        let predictiveRequestId = 0;
        let isInputActive = false;
        let isComposing = false;
        let activeItemIndex = -1;

        const helpers = {
            formatMoney: (value) => formatMoney(value, moneyFormat),
            buildSizedImageUrl,
            getProductImage,
            createArrowIcon,
        };

        const updateFooter = (queryValue) => {
            if (!footer || !getFooterData) return;
            const data = getFooterData(queryValue || '');
            if (!data) return;
            if (data.href) {
                footer.setAttribute('href', data.href);
            }
            footer.textContent = data.text || '';
            footer.appendChild(document.createTextNode(' '));
            footer.appendChild(createArrowIcon());
        };

        const setPredictiveVisible = (isVisible) => {
            panel.classList.toggle('is-visible', isVisible);
            panel.setAttribute('aria-hidden', String(!isVisible));
            if (container && containerVisibleClass) {
                container.classList.toggle(containerVisibleClass, isVisible);
            }
            if (!isVisible) {
                panel.classList.remove('is-empty');
            }
        };

        const setPredictiveEmpty = (isEmpty) => {
            panel.classList.toggle('is-empty', isEmpty);
        };

        const setPredictiveLoading = (isLoading) => {
            panel.classList.toggle('is-loading', isLoading);
            if (loading) {
                loading.setAttribute('aria-hidden', isLoading ? 'false' : 'true');
            }
        };

        const clearResults = () => {
            suggestionsList.innerHTML = '';
            productsList.innerHTML = '';
        };

        const updateClearButtonVisibility = () => {
            if (!clearButton) return;
            const hasValue = input.value.trim().length > 0;
            clearButton.hidden = !hasValue;
        };

        const getNavigableItems = () => {
            const suggestions = Array.from(suggestionsList.querySelectorAll('[data-predictive-term]'));
            const products = Array.from(productsList.querySelectorAll('a'));
            return [...suggestions, ...products];
        };

        const clearActiveItem = () => {
            if (!enableKeyboardNavigation) return;
            const items = getNavigableItems();
            items.forEach((item) => {
                item.classList.remove(keyboardActiveClass);
                item.setAttribute('aria-selected', 'false');
            });
            activeItemIndex = -1;
            input.removeAttribute('aria-activedescendant');
        };

        const setActiveItem = (index) => {
            const items = getNavigableItems();
            if (!items.length) return;

            clearActiveItem();

            if (index < 0) {
                activeItemIndex = items.length - 1;
            } else if (index >= items.length) {
                activeItemIndex = 0;
            } else {
                activeItemIndex = index;
            }

            const activeItem = items[activeItemIndex];
            if (!activeItem) return;

            activeItem.classList.add(keyboardActiveClass);
            activeItem.setAttribute('aria-selected', 'true');
            activeItem.id = activeItem.id || `${activeItemIdPrefix}-${activeItemIndex}`;
            input.setAttribute('aria-activedescendant', activeItem.id);
            activeItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        };

        const selectActiveItem = () => {
            const items = getNavigableItems();
            if (activeItemIndex < 0 || activeItemIndex >= items.length) return false;

            const activeItem = items[activeItemIndex];
            if (!activeItem) return false;

            const term = activeItem.getAttribute('data-predictive-term');
            if (term && onSubmit) {
                onSubmit(term);
                setPredictiveVisible(false);
                clearActiveItem();
                return true;
            }

            if (activeItem.tagName === 'A' && activeItem.href) {
                window.location.href = activeItem.href;
                return true;
            }

            return false;
        };

        const renderPredictive = (queryValue, suggestions, products) => {
            if (!isInputActive) {
                setPredictiveLoading(false);
                setPredictiveVisible(false);
                return;
            }
            setPredictiveLoading(false);
            suggestionsList.innerHTML = '';
            const suggestionsFragment = document.createDocumentFragment();
            suggestions.forEach((term) => {
                const item = renderSuggestion(term, helpers);
                if (item) suggestionsFragment.appendChild(item);
            });
            suggestionsList.appendChild(suggestionsFragment);

            productsList.innerHTML = '';
            const productsFragment = document.createDocumentFragment();
            products.forEach((product) => {
                const item = renderProduct(product, helpers);
                if (item) productsFragment.appendChild(item);
            });
            productsList.appendChild(productsFragment);

            updateFooter(queryValue);
            const hasResults = suggestions.length > 0 || products.length > 0;
            setPredictiveEmpty(!hasResults);
            setPredictiveVisible(true);
        };

        const getPredictiveCache = (queryKey) => predictiveCache.get(queryKey);

        const setPredictiveCache = (queryKey, payload) => {
            if (!predictiveCache.has(queryKey) && predictiveCache.size >= predictiveCacheLimit) {
                const firstKey = predictiveCache.keys().next().value;
                predictiveCache.delete(firstKey);
            }
            predictiveCache.set(queryKey, payload);
            persistPredictiveCache(queryKey, payload, predictiveCacheLimit);
        };

        const fetchPredictive = async (queryValue) => {
            if (!queryValue || queryValue.length < minLength) {
                setPredictiveLoading(false);
                setPredictiveVisible(false);
                return;
            }

            const requestId = ++predictiveRequestId;
            if (predictiveController) {
                predictiveController.abort();
            }
            predictiveController = new AbortController();
            const queryKey = queryValue.toLowerCase();
            const cachedResult = getPredictiveCache(queryKey);
            if (cachedResult) {
                setPredictiveLoading(false);
                renderPredictive(queryValue, cachedResult.suggestions, cachedResult.products);
                return;
            }
            clearResults();
            setPredictiveEmpty(false);
            setPredictiveVisible(true);
            setPredictiveLoading(true);

            try {
                const response = await fetch(buildPredictiveUrl(queryValue, suggestionLimit, productLimit), {
                    headers: {
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    signal: predictiveController.signal,
                });
                if (!response.ok) {
                    if (requestId === predictiveRequestId) {
                        setPredictiveLoading(false);
                        setPredictiveVisible(false);
                    }
                    return;
                }
                const data = await response.json();
                if (requestId !== predictiveRequestId) return;

                const results = data && data.resources && data.resources.results ? data.resources.results : {};
                const rawSuggestions = results.queries || results.query_suggestions || results.suggestions || [];
                const rawProducts = results.products || [];
                const suggestions = rawSuggestions
                    .map((item) => item.text || item.query || item.suggestion || item)
                    .filter((value) => typeof value === 'string' && value.trim() !== '')
                    .slice(0, suggestionLimit);
                const products = rawProducts.slice(0, productLimit);

                setPredictiveCache(queryKey, { suggestions, products });
                renderPredictive(queryValue, suggestions, products);
            } catch (error) {
                if (error.name === 'AbortError') return;
                setPredictiveLoading(false);
                setPredictiveVisible(false);
            }
        };

        const schedulePredictive = () => {
            const queryValue = input.value.trim();
            if (predictiveTimeout) {
                window.clearTimeout(predictiveTimeout);
            }
            if (queryValue.length < minLength) {
                if (predictiveController) {
                    predictiveController.abort();
                    predictiveController = null;
                }
                setPredictiveLoading(false);
                setPredictiveVisible(false);
                updateFooter(queryValue);
                return;
            }
            clearResults();
            setPredictiveEmpty(false);
            setPredictiveVisible(true);
            setPredictiveLoading(true);
            predictiveTimeout = window.setTimeout(() => {
                fetchPredictive(queryValue);
            }, debounceMs);
        };

        const handleCompositionStart = () => {
            isComposing = true;
        };

        const handleCompositionEnd = () => {
            isComposing = false;
            schedulePredictive();
        };

        const handleInput = () => {
            if (isComposing) {
                updateClearButtonVisibility();
                return;
            }
            schedulePredictive();
            updateClearButtonVisibility();
        };

        const handleFocus = () => {
            isInputActive = true;
            schedulePredictive();
        };

        const handleBlur = () => {
            isInputActive = false;
            // On blur, do not close immediately; close on outside click to improve mobile keyboard dismissal.
        };

        const handleDocumentClick = (event) => {
            const target = event.target;
            // If clicking the input itself or inside the panel, do not close.
            if (input.contains(target) || panel.contains(target)) return;
            // If a container exists (e.g., overlay) and the click is inside it but not input/panel, do not close.
            // For generality, when a container is provided, we typically treat clicks outside the container as a full close.
            // Standard click-outside logic: close when clicking outside the panel and input.

            // Extra check: if clicking the clear button, handleClear will handle it, so ignore here.
            if (clearButton && clearButton.contains(target)) return;

            setPredictiveVisible(false);
            clearActiveItem();
        };

        const handleClear = () => {
            input.value = '';
            updateClearButtonVisibility();
            setPredictiveVisible(false);
            updateFooter('');
            input.focus();
        };

        const handleSuggestionsClick = (event) => {
            const target = event.target instanceof Element ? event.target : null;
            if (!target) return;
            const termButton = target.closest('[data-predictive-term]');
            if (!termButton || !suggestionsList.contains(termButton)) return;
            event.preventDefault();
            const term = termButton.getAttribute('data-predictive-term') || '';
            if (term && onSubmit) {
                onSubmit(term);
            }
            setPredictiveVisible(false);
            clearActiveItem();
        };

        const handleKeydown = (event) => {
            if (!enableKeyboardNavigation) return;
            const isPanelVisible = panel.classList.contains('is-visible');
            if (!isPanelVisible) return;

            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    setActiveItem(activeItemIndex + 1);
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    setActiveItem(activeItemIndex - 1);
                    break;
                case 'Enter':
                    if (activeItemIndex >= 0) {
                        event.preventDefault();
                        selectActiveItem();
                    }
                    break;
                case 'Escape':
                    setPredictiveVisible(false);
                    clearActiveItem();
                    break;
            }
        };

        input.addEventListener('compositionstart', handleCompositionStart, { passive: true });
        input.addEventListener('compositionend', handleCompositionEnd, { passive: true });
        input.addEventListener('input', handleInput, { passive: true });
        input.addEventListener('focus', handleFocus, { passive: true });
        input.addEventListener('blur', handleBlur, { passive: true });
        input.addEventListener('keydown', handleKeydown);
        suggestionsList.addEventListener('click', handleSuggestionsClick);
        document.addEventListener('click', handleDocumentClick); // Add global click listener.

        if (clearButton) {
            clearButton.addEventListener('click', handleClear);
        }

        updateClearButtonVisibility();
        updateFooter(input.value.trim());
        setPredictiveLoading(false);
        setPredictiveVisible(false);

        return {
            refresh: () => {
                const queryValue = input.value.trim();
                updateFooter(queryValue);
                if (queryValue.length >= minLength) {
                    fetchPredictive(queryValue);
                }
            },
            hide: () => {
                setPredictiveVisible(false);
                clearActiveItem();
            },
            destroy: () => {
                input.removeEventListener('compositionstart', handleCompositionStart);
                input.removeEventListener('compositionend', handleCompositionEnd);
                input.removeEventListener('input', handleInput);
                input.removeEventListener('focus', handleFocus);
                input.removeEventListener('blur', handleBlur);
                input.removeEventListener('keydown', handleKeydown);
                suggestionsList.removeEventListener('click', handleSuggestionsClick);
                document.removeEventListener('click', handleDocumentClick); // Remove global click listener.

                if (clearButton) {
                    clearButton.removeEventListener('click', handleClear);
                }
            },
        };
    };

    window.EEGPredictiveSearch = {
        create: createPredictiveSearch,
        createArrowIcon,
        formatMoney,
        buildSizedImageUrl,
        getProductImage,
    };
})();
